import type { ErrorEvent } from '@sentry/react';
import { isNonEmptyString, matchStrings } from '@sortlist-frontend/utils';

import { IGNORED_CLIENT_ERRORS, INJECTED_SCRIPT_URLS } from '../sentry.client';

export type EnhancedEvent = ErrorEvent & {
  title?: string;
  metadata?: {
    type?: string;
    value?: string;
    filename?: string;
  };
  errors?: {
    url?: string;
  }[];
  location?: string;
};

const IGNORED_ERRORS_WITH_CONFIG: IgnoredErrorsConfig[] = [
  // Ignore 403 errors on tracking endpoints - these are expected when Cloudflare blocks
  // automated requests from in-app browsers (Snapchat, Facebook, Chrome WebView, etc.)
  {
    errorMessage: /Request failed with status code 403/i,
    affectedUrls: [/\/api\/clue/, /\/api\/proxy/],
  },
  // Ignore errors from Webview and TikTok browsers
  { affectedBrowsers: [/Webview/i, /Tiktok/i] },
  {
    errorMessage: /NotFoundError: Failed to execute 'removeChild' on 'Node'/i,
    affectedBrowsers: ['Edge', 'Opera'],
  },
  {
    errorMessage: /private fields are not currently supported/i,
    affectedBrowsers: ['Firefox'],
  },
  // Google maps safari errors
  { errorTrace: /(maps-api-v3|maps\/api\/js)/, affectedBrowsers: ['Safari', 'Mobile Safari'] },
  // Fetch failed on safari
  {
    errorMessage: /Load failed/i,
    affectedBrowsers: ['Safari', 'Mobile Safari'],
  },
  {
    errorMessage: /Growthbook/i,
    affectedBrowsers: ['Safari', 'Mobile Safari'],
  },
  {
    errorMessage: /.+document.elementFromPoint.+/i,
    affectedDevices: ['iPhone'],
  },
];

export function isIgnoredError(error: string) {
  return IGNORED_CLIENT_ERRORS.some((ignoredError) => matchStrings(ignoredError, error));
}

const UNRESOLVED_FRAME_FILENAMES = ['<anonymous>', '[native code]'];

// Sentry orders frames oldest first, so the deepest frame resolving to a file is the one that threw.
function getThrowingFrameFilename(event: EnhancedEvent): string | undefined {
  const frames = event.exception?.values?.[0]?.stacktrace?.frames ?? [];

  for (let index = frames.length - 1; index >= 0; index--) {
    const filename = frames[index]?.filename;

    if (isNonEmptyString(filename) && !UNRESOLVED_FRAME_FILENAMES.includes(filename)) return filename;
  }

  return undefined;
}

// Only the frame that threw decides ownership: a stack that merely passes through an injected
// script before failing in a Sortlist bundle stays reported.
function isFromInjectedScript(event: EnhancedEvent): boolean {
  const filename = getThrowingFrameFilename(event);

  return isNonEmptyString(filename) && INJECTED_SCRIPT_URLS.some((url) => url.test(filename));
}

export function isIgnoredEvent(event: EnhancedEvent): boolean {
  return (
    IGNORED_CLIENT_ERRORS.some((ignoredError) => matchEventToErrorMessage(event, ignoredError)) ||
    isFromInjectedScript(event) ||
    shouldIgnoreError(event)
  );
}

const isErrorFromBrowser = (event: EnhancedEvent, affectedBrowsers: (string | RegExp)[]) => {
  const eventBrowserName = event.contexts?.browser?.name;
  if (!isNonEmptyString(eventBrowserName)) return false;

  return affectedBrowsers.some((browser) => {
    return typeof browser === 'string' ? eventBrowserName.includes(browser) : browser.test(eventBrowserName);
  });
};

const isErrorFromDevice = (event: EnhancedEvent, affectedDevices: (string | RegExp)[]) => {
  const eventDeviceName = event.contexts?.device?.family;
  if (!isNonEmptyString(eventDeviceName)) return false;

  return affectedDevices.some((device) => {
    return typeof device === 'string' ? eventDeviceName.includes(device) : device.test(eventDeviceName);
  });
};

const isErrorFromUrl = (event: EnhancedEvent, affectedUrls: RegExp[]) => {
  if (affectedUrls.length === 0) return false;

  // Check the request URL from the event
  const requestUrl = event.request?.url;

  // Also check breadcrumbs for fetch/xhr requests that failed
  const breadcrumbUrls =
    event.breadcrumbs
      ?.filter((b) => b.category === 'fetch' || b.category === 'xhr')
      .map((b) => b.data?.url as string | undefined)
      .filter((url): url is string => isNonEmptyString(url)) ?? [];

  const allUrls = [requestUrl, ...breadcrumbUrls].filter((url): url is string => isNonEmptyString(url));

  return allUrls.some((url) => affectedUrls.some((pattern) => pattern.test(url)));
};

type IgnoredErrorsConfig = {
  errorMessage?: string | RegExp;
  errorTrace?: RegExp;
  affectedBrowsers?: (RegExp | string)[];
  affectedDevices?: (RegExp | string)[];
  affectedUrls?: RegExp[];
};

function shouldIgnoreError(event: EnhancedEvent): boolean {
  return IGNORED_ERRORS_WITH_CONFIG.some((ignoredError) => {
    const { errorTrace, errorMessage, affectedBrowsers = [], affectedDevices = [], affectedUrls = [] } = ignoredError;

    const isBrowserMatch = isErrorFromBrowser(event, affectedBrowsers);
    const isDeviceMatch = isErrorFromDevice(event, affectedDevices);
    const isUrlMatch = isErrorFromUrl(event, affectedUrls);

    // Check if the error matches the URL filter (for endpoint-specific errors like 403s)
    if (
      affectedUrls.length > 0 &&
      isUrlMatch &&
      errorMessage != null &&
      matchEventToErrorMessage(event, errorMessage)
    ) {
      return true;
    }

    // If neither the device nor the browser match, the error should not be ignored
    if (!isDeviceMatch && !isBrowserMatch) return false;

    return (
      (errorMessage != null && matchEventToErrorMessage(event, errorMessage)) ||
      (errorTrace != null && matchEventToStackTrace(event, errorTrace))
    );
  });
}

function matchEventToStackTrace(event: EnhancedEvent, trace: string | RegExp): boolean {
  const eventTraces: string[] = (event.errors?.map((error) => error.url).filter((x) => x != null) ??
    event.exception?.values?.[0]?.stacktrace?.frames?.map((frame) => frame.filename).filter((x) => x != null) ??
    []) as string[];

  const eventTrace = event.location ?? event.metadata?.filename;

  return (
    (isNonEmptyString(eventTrace) && matchStrings(eventTrace, trace)) ||
    eventTraces.some((eventTrace) => matchStrings(eventTrace, trace))
  );
}

function matchEventToErrorMessage(event: EnhancedEvent, error: string | RegExp): boolean {
  const { title, metadata, message, exception, extra } = event;
  // type is often the title of the error, and value the message. So we should NOT check only the type
  const { value } = metadata ?? {};
  // We assume the first entry is the most relevant
  const errorFromException = exception?.values?.[0]?.value;

  // This is something we pass as custom data sometimes
  const errorFromExtra = (extra as { error?: Error })?.error?.message;

  return (
    (isNonEmptyString(title) && matchStrings(title, error)) ||
    (isNonEmptyString(value) && matchStrings(value, error)) ||
    (isNonEmptyString(message) && matchStrings(message, error)) ||
    (isNonEmptyString(errorFromExtra) && matchStrings(errorFromExtra, error)) ||
    (isNonEmptyString(errorFromException) && matchStrings(errorFromException, error))
  );
}
